import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, AuthCallback, ProtectedRoute } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import IntroducingPage from "./pages/IntroducingPage";
import ProblemPage from "./pages/ProblemPage";
import SolutionPage from "./pages/SolutionPage";
import ImportantPage from "./pages/ImportantPage";
import PricingPage from "./pages/PricingPage";
import ExamplesPage from "./pages/ExamplesPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CRMPage from "./pages/CRM/CRMPage";
import AdminPage from "./pages/AdminPage";
import RecipePage from "./pages/RecipePage";
import DownloadPage from "./pages/DownloadPage";
import ManualPage from "./pages/ManualPage";
import InstallPage from "./pages/InstallPage";
import PipDashboardPage from "./pages/PipDashboardPage";
import UpdateLogPage from "./pages/UpdateLogPage";
import PressReleasePage from "./pages/PressReleasePage";
import NewsPreferencesPage from "./pages/NewsPreferencesPage";
import InvitePage from "./pages/InvitePage";

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/introducing" element={<IntroducingPage />} />
      <Route path="/problem" element={<ProblemPage />} />
      <Route path="/solution" element={<SolutionPage />} />
      <Route path="/important" element={<ImportantPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/examples" element={<ExamplesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/crm" element={<CRMPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/recipe" element={<RecipePage />} />
      <Route path="/downloads" element={<DownloadPage />} />
      <Route path="/manual" element={<ManualPage />} />
      <Route path="/manuals" element={<ManualPage />} />
      <Route path="/install" element={<InstallPage />} />
      <Route path="/pip-dashboard" element={<ProtectedRoute><PipDashboardPage /></ProtectedRoute>} />
      <Route path="/update-log" element={<UpdateLogPage />} />
      <Route path="/press" element={<PressReleasePage />} />
      <Route path="/news/preferences" element={<ProtectedRoute><NewsPreferencesPage /></ProtectedRoute>} />
      <Route path="/invite/:code" element={<InvitePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Global 402 interceptor — shows upgrade toast & redirects on trial-locked mutations.
  // Registered once at App mount.
  if (!window.__topscreenAxiosInterceptor) {
    window.__topscreenAxiosInterceptor = true;
    axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) {
          toast.error(err.response.data?.detail || "Trial expired — upgrade to lifetime ($39).", {
            action: { label: "Upgrade", onClick: () => { window.location.href = "/pricing"; } },
          });
        }
        return Promise.reject(err);
      }
    );
  }
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
